/**
 * AutomationsView (COMMS ARC, 2026-08-13) — the office's window into automated
 * comms. Top: the honest test-mode banner (comms_live). Left: each automation
 * as a card — on/off, editable template with {{tokens}}, timing config. Right:
 * the OUTBOX — every message the system would have sent (held), will send
 * (queued), or did send, with a cancel for anything unsent. This is where Seth
 * watches a week of would-have-sent traffic before ever flipping live.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AlertTriangle, Ban, CheckCircle2, ChevronDown, Clock, Mail, MessageSquare,
  Moon, Save, Send, ShieldOff, Zap,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { formatDateTimeUS } from "@/lib/format";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { supabase } from "@/lib/supabase";
import { useCurrentUserEmail } from "@/features/inbox/api";
import {
  useCommsAutomations, useSaveAutomation, useCommsLive, useOutbox, useOutboxCounts,
  useCancelOutbox, useQuietHours, useSaveQuietHours, type CommsAutomation, type OutboxRow,
} from "./comms";

const STATUS_STYLE: Record<string, string> = {
  held: "bg-amber-50 text-amber-700 border-amber-200",
  queued: "bg-blue-50 text-blue-700 border-blue-200",
  sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  suppressed: "bg-slate-100 text-slate-500 border-slate-200",
  cancelled: "bg-slate-100 text-slate-400 border-slate-200",
};

function Toggle({ on, onChange, disabled }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={cn(
        "relative h-6 w-11 shrink-0 rounded-full transition-colors",
        on ? "bg-emerald-500" : "bg-slate-300",
        disabled && "opacity-50",
      )}
    >
      <span className={cn("absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all", on ? "left-[22px]" : "left-0.5")} />
    </button>
  );
}

// ---------------------------------------------------------------------------
// TEST-TO-ME-01 (Seth 2026-10-01: "send me example emails from pegasus of
// what post install he would receive… just to test") — every automation card
// gets "Email me a test": the template AS IT READS ON SCREEN (unsaved tweaks
// included), sample details filled, delivered to YOUR inbox through
// send-email's operator-test path. Never a customer; real links (survey /
// review / portal base) come from org settings so what you see is what
// ships. A text automation arrives as an email SHOWING the text's wording.
// ---------------------------------------------------------------------------
const TEST_SAMPLE: Record<string, string> = {
  first_name: "Sam",
  date: "Friday, October 9",
  window_phrase: ", arrival window 10–12",
  tech_first: "Jeremy",
  address: "1234 Main St",
  city: "Lakeville",
  invoice_no: "SH-2041",
  balance: "$1,284.00",
  plan: "PLUS",
  expire_date: "November 30, 2026",
  title: "Furnace & AC replacement",
  utility: "CenterPoint Energy",
  rebate_confirmation: "CP-558201",
  rebate_amount_line:
    '<p style="color:#5f6b7a;font-size:15px;line-height:1.6;margin:0 0 6px;text-align:center">Estimated rebate: <b style="color:#16314f">$400</b> — the final amount is determined by the utility.</p>',
};

/** One automation's editable card. */
function AutomationCard({ a }: { a: CommsAutomation }) {
  // PERM: editing automations (toggles + templates + timing) is its own
  // capability; everyone with the page can READ what's configured.
  const canManage = useCan("automations.manage");
  const save = useSaveAutomation();
  const [enabled, setEnabled] = useState(a.enabled);
  const [sms, setSms] = useState(a.template_sms ?? "");
  const [subj, setSubj] = useState(a.template_email_subject ?? "");
  const [body, setBody] = useState(a.template_email_body ?? "");
  const [cfg, setCfg] = useState<Record<string, unknown>>(a.config ?? {});
  useEffect(() => {
    setEnabled(a.enabled); setSms(a.template_sms ?? ""); setSubj(a.template_email_subject ?? "");
    setBody(a.template_email_body ?? ""); setCfg(a.config ?? {});
  }, [a]);
  const dirty =
    sms !== (a.template_sms ?? "") || subj !== (a.template_email_subject ?? "") ||
    body !== (a.template_email_body ?? "") || JSON.stringify(cfg) !== JSON.stringify(a.config ?? {});

  const toggle = (v: boolean) => {
    setEnabled(v);
    save.mutate({ key: a.key, enabled: v });
  };
  const doSave = () => {
    save.mutate({
      key: a.key,
      template_sms: a.channel === "sms" ? sms || null : a.template_sms,
      template_email_subject: a.channel === "email" ? subj || null : a.template_email_subject,
      template_email_body: a.channel === "email" ? body || null : a.template_email_body,
      config: cfg as CommsAutomation["config"],
    });
  };
  const num = (k: string, fallback: number) => Number((cfg as Record<string, unknown>)[k] ?? fallback);
  const setNum = (k: string, v: number) => setCfg((c) => ({ ...c, [k]: v }));

  // TEST-TO-ME-01: this template, sample-filled, to MY inbox — never a customer.
  const myEmail = useCurrentUserEmail().data ?? null;
  const [tested, setTested] = useState(false);
  const sendTest = useMutation({
    mutationFn: async () => {
      if (!myEmail) throw new Error("No signed-in email.");
      const { data: st } = await supabase.from("org_setting").select("key, value")
        .in("key", ["survey_link", "feedback_link", "review_link", "referral_terms", "public_app_url"]);
      const sv = new Map(((st ?? []) as { key: string; value: unknown }[]).map((r) => [r.key, String(r.value ?? "").replace(/^"|"$/g, "")]));
      const reviewLink = sv.get("review_link") || "https://www.sedgwickheating.com";
      const portal = (sv.get("public_app_url") || "https://my.sedgwickheating.com").replace(/\/+$/, "");
      const tokens: Record<string, string> = {
        ...TEST_SAMPLE,
        survey_link: sv.get("survey_link") || sv.get("feedback_link") || "https://www.sedgwickheating.com",
        referral_terms: sv.get("referral_terms") || "Refer a friend: $50 for you, $50 off for them.",
        review_link: reviewLink,
        portal_url: portal,
        proposal_url: `${portal}/proposal`,
        warranty_link: portal,
        review_line: `And if we earned it, a quick <a href="${reviewLink}">Google review</a> means the world to our team.`,
      };
      const fill = (s: string) => s.replace(/\{\{(\w+)\}\}/g, (_m, k) => tokens[k] ?? `{{${k}}}`);
      const emailSubj = subj || a.template_email_subject || "";
      const emailBody = body || a.template_email_body || "";
      const asEmail = !!emailBody;
      const subject = `[Pegasus test] ${asEmail && emailSubj ? fill(emailSubj) : `${a.label} — text-message wording`}`;
      const html = asEmail
        ? fill(emailBody)
        : `<div style="font-family:system-ui,-apple-system,sans-serif;max-width:480px;margin:0 auto;padding:16px"><p style="font-size:13px;color:#64748b;margin:0 0 10px">This automation goes out as a TEXT message. It reads:</p><div style="border:1px solid #e2e8f0;border-radius:14px;padding:14px 16px;font-size:15px;line-height:1.55;color:#0f172a">${fill(sms || a.template_sms || "")}</div></div>`;
      const { data, error } = await supabase.functions.invoke("send-email", {
        body: { to: [myEmail], subject, html, message_type: "marketing-test" },
      });
      if (error) throw new Error(error.message);
      const err = (data as { error?: string } | null)?.error;
      if (err) throw new Error(err);
    },
    onSuccess: () => {
      setTested(true);
      window.setTimeout(() => setTested(false), 4000);
    },
  });

  // review_request carries the one-rail warning — make it visually loud.
  const isReviewRail = a.key === "review_request";

  return (
    <Card className={cn("p-4", !enabled && "opacity-75")}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          {a.channel === "sms" ? <MessageSquare className="h-4 w-4 shrink-0 text-brand-500" /> : <Mail className="h-4 w-4 shrink-0 text-brand-500" />}
          <h3 className="truncate text-sm font-semibold text-slate-900">{a.label}</h3>
        </div>
        <Toggle on={enabled} onChange={toggle} disabled={save.isPending || !canManage} />
      </div>
      {a.description && (
        <p className={cn("mt-1.5 text-xs leading-relaxed", isReviewRail ? "text-amber-700" : "text-slate-500")}>
          {isReviewRail && <AlertTriangle className="mr-1 inline h-3.5 w-3.5 align-[-2px]" />}
          {a.description}
        </p>
      )}

      {a.channel === "sms" ? (
        <textarea
          value={sms}
          onChange={(e) => setSms(e.target.value)}
          readOnly={!canManage}
          rows={3}
          className="mt-3 w-full rounded-lg border border-hairline px-3 py-2 text-sm leading-relaxed focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
        />
      ) : (
        <div className="mt-3 space-y-2">
          <input
            type="text"
            value={subj}
            onChange={(e) => setSubj(e.target.value)}
            readOnly={!canManage}
            placeholder="Email subject"
            className="w-full rounded-lg border border-hairline px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            readOnly={!canManage}
            rows={5}
            className="w-full rounded-lg border border-hairline px-3 py-2 font-mono text-xs leading-relaxed focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>
      )}

      {/* Timing knobs per automation */}
      <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-600">
        {a.key === "appt_reminder" && (
          <>
            <label className="inline-flex items-center gap-1.5">
              Days before
              <input type="number" min={1} max={7} value={num("days_before", 1)} onChange={(e) => setNum("days_before", Number(e.target.value))}
                className="w-14 rounded-md border border-hairline px-1.5 py-1 text-center" />
            </label>
            <label className="inline-flex items-center gap-1.5">
              Send at
              <input type="number" min={7} max={20} value={num("at_hour", 16)} onChange={(e) => setNum("at_hour", Number(e.target.value))}
                className="w-14 rounded-md border border-hairline px-1.5 py-1 text-center" />
              :00
            </label>
          </>
        )}
        {a.key === "review_request" && (
          <label className="inline-flex items-center gap-1.5">
            Hours after completion
            <input type="number" min={0} max={48} value={num("delay_hours", 2)} onChange={(e) => setNum("delay_hours", Number(e.target.value))}
              className="w-14 rounded-md border border-hairline px-1.5 py-1 text-center" />
          </label>
        )}
        {a.key === "invoice_reminder" && (
          <label className="inline-flex items-center gap-1.5">
            Nudge at days
            <input
              type="text"
              value={((cfg as { steps_days_after_issue?: number[] }).steps_days_after_issue ?? [10, 30]).join(", ")}
              onChange={(e) => setCfg((c) => ({ ...c, steps_days_after_issue: e.target.value.split(/[,\s]+/).map(Number).filter((n) => isFinite(n) && n > 0) }))}
              className="w-24 rounded-md border border-hairline px-1.5 py-1 text-center"
            />
          </label>
        )}
        <span className="text-slate-400">
          Tokens: {a.key.startsWith("appt") ? "{{first_name}} {{date}} {{window_phrase}} {{tech_first}} {{address}}" :
            a.key === "review_request" ? "{{first_name}} {{tech_first}} {{review_link}}" :
            "{{first_name}} {{invoice_no}} {{balance}} {{portal_url}}"}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {dirty && canManage && (
          <Button variant="primary" size="sm" loading={save.isPending} onClick={doSave}>
            <Save className="h-3.5 w-3.5" /> Save changes
          </Button>
        )}
        {canManage && (
          <Button
            variant="secondary"
            size="sm"
            loading={sendTest.isPending}
            onClick={() => sendTest.mutate()}
            title={`Sends this ${a.channel === "sms" ? "text's wording (as an email preview)" : "email"} to ${myEmail ?? "your inbox"} with sample details filled in — never to a customer`}
          >
            {tested ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Send className="h-3.5 w-3.5" />}
            {tested ? "Sent to you" : "Email me a test"}
          </Button>
        )}
        {sendTest.isError && (
          <span className="text-xs text-rose-600">{String((sendTest.error as Error)?.message ?? "test failed")}</span>
        )}
      </div>
    </Card>
  );
}

function QuietHoursCard() {
  const { data: qh } = useQuietHours();
  const save = useSaveQuietHours();
  const [start, setStart] = useState(8);
  const [end, setEnd] = useState(20);
  useEffect(() => { if (qh) { setStart(qh.start); setEnd(qh.end); } }, [qh]);
  const dirty = qh && (start !== qh.start || end !== qh.end);
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2">
        <Moon className="h-4 w-4 text-brand-500" />
        <h3 className="text-sm font-semibold text-slate-900">Quiet Hours</h3>
      </div>
      <p className="mt-1.5 text-xs text-slate-500">
        No automated message ever lands outside this window — anything triggered late waits for the next morning.
      </p>
      <div className="mt-3 flex items-center gap-3 text-sm text-slate-700">
        <label className="inline-flex items-center gap-1.5">
          From
          <input type="number" min={5} max={12} value={start} onChange={(e) => setStart(Number(e.target.value))}
            className="w-14 rounded-md border border-hairline px-1.5 py-1 text-center text-xs" />
          :00
        </label>
        <label className="inline-flex items-center gap-1.5">
          to
          <input type="number" min={15} max={23} value={end} onChange={(e) => setEnd(Number(e.target.value))}
            className="w-14 rounded-md border border-hairline px-1.5 py-1 text-center text-xs" />
          :00
        </label>
        {dirty && (
          <Button variant="primary" size="sm" loading={save.isPending} onClick={() => save.mutate({ start, end })}>
            <Save className="h-3.5 w-3.5" /> Save
          </Button>
        )}
      </div>
    </Card>
  );
}

function OutboxPanel() {
  const [status, setStatus] = useState("all");
  const [automation, setAutomation] = useState("all");
  const [expanded, setExpanded] = useState<string | null>(null);
  const { data: rows } = useOutbox({ status, automation });
  const { data: counts } = useOutboxCounts();
  const { data: autos } = useCommsAutomations();
  const cancel = useCancelOutbox();

  const chips: { v: string; label: string }[] = useMemo(() => [
    { v: "all", label: "All" },
    { v: "held", label: `Held ${counts?.held ?? 0}` },
    { v: "queued", label: `Queued ${counts?.queued ?? 0}` },
    { v: "sent", label: `Sent ${counts?.sent ?? 0}` },
    { v: "suppressed", label: `Suppressed ${counts?.suppressed ?? 0}` },
    { v: "failed", label: `Failed ${counts?.failed ?? 0}` },
  ], [counts]);

  return (
    <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="border-b border-hairline bg-slate-50/70 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Send className="h-4 w-4 text-brand-500" /> Outbox
          </h3>
          <select
            value={automation}
            onChange={(e) => setAutomation(e.target.value)}
            className="rounded-md border border-hairline bg-white px-2 py-1 text-xs"
          >
            <option value="all">All automations</option>
            {(autos ?? []).map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
            <option value="invoice_send">Invoice email (manual)</option>
          </select>
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {chips.map((c) => (
            <button
              key={c.v}
              type="button"
              onClick={() => setStatus(c.v)}
              className={cn(
                "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                status === c.v ? "border-brand-300 bg-brand-50 text-brand-700" : "border-hairline bg-white text-slate-500 hover:bg-slate-50",
              )}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {(rows ?? []).length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-slate-400">
            Nothing here yet — as visits get scheduled and invoices age, the traffic the system
            <em> would</em> send shows up in this list.
          </div>
        ) : (
          <div className="divide-y divide-hairline">
            {(rows ?? []).map((m: OutboxRow) => {
              const open = expanded === m.id;
              return (
                <div key={m.id} className="px-4 py-2.5 text-sm">
                  <div className="flex items-center gap-2">
                    {m.channel === "sms" ? <MessageSquare className="h-3.5 w-3.5 shrink-0 text-slate-400" /> : <Mail className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                    <button type="button" onClick={() => setExpanded(open ? null : m.id)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                      <span className="min-w-0 truncate font-medium text-slate-800">{m.customer_name ?? m.to_address}</span>
                      <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                        {(m.automation_key || "").replace(/_/g, " ")}
                      </span>
                      {/* COMMS-CUTOVER-01: a row raised by work booked in ESC (the mirror), not in Pegasus. */}
                      {m.trigger_kind === "esc_dispatch" && (
                        <span className="shrink-0 rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700" title={`ESC dispatch ${m.trigger_ref ?? ""}`}>ESC</span>
                      )}
                      <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 text-slate-300 transition-transform", open && "rotate-180")} />
                    </button>
                    <span className={cn("shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide", STATUS_STYLE[m.status] ?? "")}>
                      {m.status}
                    </span>
                    {(m.status === "held" || m.status === "queued") && (
                      <button
                        type="button"
                        title="Cancel — never send this one"
                        onClick={() => cancel.mutate(m.id)}
                        className="shrink-0 rounded-md p-1 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-600"
                      >
                        <Ban className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 pl-[22px] text-[11px] text-slate-400">
                    <span>{m.to_address}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {m.status === "sent" && m.sent_at ? `sent ${formatDateTimeUS(m.sent_at)}` : `for ${formatDateTimeUS(m.scheduled_for)}`}</span>
                    {m.status_detail && <span className="text-amber-600">{m.status_detail}</span>}
                  </div>
                  {open && (
                    <div className="ml-[22px] mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-700">
                      {m.subject && <div className="mb-1 font-semibold">{m.subject}</div>}
                      {m.channel === "email" ? (
                        <div className="prose-sm max-w-none [&_a]:text-brand-700" dangerouslySetInnerHTML={{ __html: m.body }} />
                      ) : (
                        <span className="whitespace-pre-wrap">{m.body}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}

export function AutomationsView() {
  const { data: live } = useCommsLive();
  const { data: autos } = useCommsAutomations();
  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="inline-flex items-center gap-2 text-xl font-bold text-slate-900">
            <Zap className="h-5 w-5 text-brand-500" /> Comms &amp; Automations
          </h1>
          <p className="mt-0.5 text-sm text-slate-500">
            The messages Pegasus sends on its own — and the outbox showing exactly what it would say.
          </p>
        </div>
        {live ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> LIVE — messages send for real
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
            <ShieldOff className="h-3.5 w-3.5" /> TESTING — nothing sends; traffic is held for review
          </span>
        )}
      </div>

      {!live && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="font-semibold">Test mode.</span> Every message an automation would send lands in the
          outbox as <span className="font-semibold">Held</span> so you can read exactly what customers would get and
          tune the wording and timing. Nothing reaches a real customer until comms goes live — and held test traffic
          stays held even after the flip (only new messages send).
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 xl:grid-cols-[420px_minmax(0,1fr)]">
        <div className="min-h-0 space-y-3 overflow-auto pr-1">
          {(autos ?? []).map((a) => <AutomationCard key={a.key} a={a} />)}
          <QuietHoursCard />
        </div>
        <OutboxPanel />
      </div>
    </div>
  );
}
